import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { adminAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from 'react-i18next';
import {
  CreditCard,
  Plus,
  CheckCircle2,
  Search,
  FileText,
  RefreshCw,
  Database,
  Upload,
  X,
  Loader2,
} from 'lucide-react';
import { formatLocalizedDateTime } from '../utils/i18nFormatters';
import {
  getPhysicalCardDisplayBalance,
  getTrialCardPresentation,
  isTrialCard,
  toRoundedPhpAmount
} from '../utils/trialCardPresentation';
import { AdminModalSurface } from './AdminModal';
import { useSearchParams } from 'react-router-dom';
import { canManagePhysicalCardRecords } from '../utils/physicalCardPermissions';
import { hasAdminPermission } from '../utils/adminPermissions';

export default function CardManagement() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const financeView = searchParams.get('view');
  const currentUser = useAuthStore((state) => state.user);
  // Removed legacy station and token-batch state.
  const [physicalCards, setPhysicalCards] = useState([]);
  const [physicalSummary, setPhysicalSummary] = useState({ totalCards: 0, boundCards: 0, totalBalance: 0 });
  const [physicalLoading, setPhysicalLoading] = useState(false);
  const [newRfid, setNewRfid] = useState('');
  const [newCardNumber, setNewCardNumber] = useState('');
  const [newInitialValue, setNewInitialValue] = useState('');
  const [physicalMsg, setPhysicalMsg] = useState(null);
  const [krialCardLookup, setKrialCardLookup] = useState('');
  const [krialTrialStartAt, setKrialTrialStartAt] = useState('');
  const [krialLoading, setKrialLoading] = useState(false);
  const [krialMsg, setKrialMsg] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'Active' | 'Inactive'
  const [bindingStatusFilter, setBindingStatusFilter] = useState('all'); // 'all' | 'bound' | 'unbound'
  const [balanceModal, setBalanceModal] = useState(null); // { operation: 'write' | 'reset' }
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceScope, setBalanceScope] = useState('unwritten_unbound');
  const [balanceReason, setBalanceReason] = useState('');
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceResult, setBalanceResult] = useState(null);
  const [selectedPhysicalCardIds, setSelectedPhysicalCardIds] = useState(new Set());
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
  const [balanceRangeStart, setBalanceRangeStart] = useState('');
  const [balanceRangeEnd, setBalanceRangeEnd] = useState('');
  const [balancePreview, setBalancePreview] = useState(null);
  const [balancePreviewLoading, setBalancePreviewLoading] = useState(false);
  const [showBatchPhysical, setShowBatchPhysical] = useState(false);
  const [batchFile, setBatchFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [batchPreview, setBatchPreview] = useState([]);
  const [batchError, setBatchError] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const batchFileRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Pagination state.
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCards, setTotalCards] = useState(0);
  const canManagePhysicalCardBalance = hasAdminPermission(currentUser, 'write_physical_card_value');
  const canManagePhysicalCards = canManagePhysicalCardRecords(currentUser);
  // Removed legacy import-dialog state.
  // const [importCardNo, setImportCardNo] = useState('');
  // const [importLoading, setImportLoading] = useState(false);
  // const [importMessage, setImportMessage] = useState(null);

  const loadPhysicalCards = useCallback(async (page = 1, searchOverride) => {
    setPhysicalLoading(true);
    setPhysicalMsg(null);
    try {
      const effectiveSearch = searchOverride === undefined ? appliedSearchQuery : searchOverride;
      const res = await adminAPI.getPhysicalCards({
        page,
        limit: 50,
        status: statusFilter,
        bindingStatus: bindingStatusFilter,
        search: effectiveSearch.trim() || undefined
      });
      if (res.data?.success) {
        setPhysicalCards(res.data.data);
        setCurrentPage(res.data.pagination.page);
        setTotalPages(res.data.pagination.totalPages);
        setTotalCards(res.data.pagination.total);
        setPhysicalSummary(res.data.summary || {
          totalCards: res.data.pagination.total || 0,
          boundCards: (res.data.data || []).filter(isCardBoundToUser).length,
          totalBalance: (res.data.data || []).reduce((acc, card) => acc + getPhysicalCardDisplayBalance(card), 0)
        });
      }
    } catch (error) {
      console.error('Failed to load physical cards:', error);
      if (error.response?.status === 403 || error.response?.status === 404) {
        setPhysicalCards([]);
        setPhysicalSummary({ totalCards: 0, boundCards: 0, totalBalance: 0 });
        setTotalCards(0);
        setTotalPages(1);
        setPhysicalMsg({ type: 'error', text: 'Your current permission roles do not allow viewing physical cards. Ask a Super Admin to assign a role with “view physical cards”, then refresh this page.' });
      } else {
        setPhysicalMsg({ type: 'error', text: 'Unable to load physical cards. Please try again.' });
      }
    } finally {
      setPhysicalLoading(false);
    }
  }, [appliedSearchQuery, bindingStatusFilter, statusFilter]);

  useEffect(() => {
    // Only physical cards are loaded here.
    loadPhysicalCards(1);
  }, [loadPhysicalCards]);

  const formatCardAmount = (value) => `₱${toRoundedPhpAmount(value).toLocaleString('en-PH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;

  const formatCardDate = (value) => {
    if (!value) return <span className="text-slate-500">-</span>;
    return formatLocalizedDateTime(value, i18n.language, { hour: undefined, minute: undefined });
  };

  const isCardBoundToUser = (card) => Boolean(card.userId) || card.status === 'Linked';
  const isCardAmountWritten = (card) => Number(card.balance || 0) > 0 || Number(card.initialValue || 0) > 0 || Number(card.cardBalance || 0) > 0;
  const isCardEligibleForBalanceSelection = (card) => (
    ['Active', 'Linked'].includes(card.status) && !isTrialCard(card)
  );

  const selectedPhysicalCardCount = selectedPhysicalCardIds.size;
  const visibleSelectableCards = canManagePhysicalCardBalance
    ? physicalCards.filter(isCardEligibleForBalanceSelection)
    : [];
  const allVisibleSelectableChecked = visibleSelectableCards.length > 0
    && visibleSelectableCards.every(card => selectedPhysicalCardIds.has(card.id));

  const togglePhysicalCardSelection = (cardId, checked) => {
    if (!canManagePhysicalCardBalance) return;
    setSelectedPhysicalCardIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(cardId);
      } else {
        next.delete(cardId);
      }
      return next;
    });
  };

  const toggleVisiblePhysicalCards = (checked) => {
    if (!canManagePhysicalCardBalance) return;
    setSelectedPhysicalCardIds(prev => {
      const next = new Set(prev);
      visibleSelectableCards.forEach(card => {
        if (checked) {
          next.add(card.id);
        } else {
          next.delete(card.id);
        }
      });
      return next;
    });
  };

  const handleAddPhysicalCard = async () => {
    if (!canManagePhysicalCards) return;
    if (!newRfid.trim()) return;
    setPhysicalMsg(null);
    try {
      const payload = { rfid: newRfid.trim().toUpperCase() };
      if (newCardNumber.trim()) payload.cardNumber = newCardNumber.trim();
      if (newInitialValue.trim()) {
        const initialValue = Number(newInitialValue);
        if (initialValue > 0 && !canManagePhysicalCardBalance) {
          setPhysicalMsg({
            type: 'error',
            text: t('cards.physical.messages.amountUnavailable', { defaultValue: 'Card amount cannot be set by this account' })
          });
          return;
        }
        payload.value = initialValue;
      }
      const res = await adminAPI.addPhysicalCard(payload);
      if (res.data?.success) {
        setPhysicalMsg({
          type: 'success',
          text: t('cards.physical.messages.addSuccess', {
            cardNumber: newCardNumber.trim() || t('cards.physical.table.notEntered')
          })
        });
        setNewRfid('');
        setNewCardNumber('');
        setNewInitialValue('');
        loadPhysicalCards();
      }
    } catch (error) {
      const msg = error.response?.data?.message || t('cards.physical.messages.addFailed');
      setPhysicalMsg({ type: 'error', text: msg });
    }
  };

  const getKrialStatus = (card) => {
    const trial = getTrialCardPresentation(card);
    if (!trial.isTrialCard) {
      return {
        label: t('cards.physical.krial.notKrial'),
        className: 'bg-slate-500/20 text-slate-400'
      };
    }

    if (trial.status === 'active') {
      return {
        label: t('cards.physical.krial.trialActive', { days: trial.daysRemaining }),
        className: 'bg-cyan-500/20 text-cyan-300'
      };
    }

    return {
      label: t('cards.physical.krial.expired'),
      className: 'bg-slate-500/20 text-slate-300'
    };
  };

  const handleAssignKrialTrial = async () => {
    if (!canManagePhysicalCards) return;
    const lookup = krialCardLookup.trim();
    if (!lookup) {
      setKrialMsg({ type: 'error', text: t('cards.physical.krial.inputRequired') });
      return;
    }

    setKrialLoading(true);
    setKrialMsg(null);
    try {
      const payload = { cardNumber: lookup };
      if (krialTrialStartAt) {
        payload.trialStartAt = krialTrialStartAt;
      }
      const res = await adminAPI.assignPhysicalCardKrialTrial(payload);
      const assignedCard = res.data?.data;
      const assignedNumber = assignedCard?.cardNumber || assignedCard?.rfid || lookup;
      setKrialMsg({
        type: 'success',
        text: t('cards.physical.krial.assignSuccess', { cardNumber: assignedNumber })
      });
      setSearchQuery(assignedNumber);
      setAppliedSearchQuery(assignedNumber);
      setCurrentPage(1);
      await loadPhysicalCards(1, assignedNumber);
    } catch (error) {
      setKrialMsg({
        type: 'error',
        text: error.response?.data?.message || t('cards.physical.krial.assignFailed')
      });
    } finally {
      setKrialLoading(false);
    }
  };

  const parseCardFile = async (file) => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    const result = [];
    const errors = [];
    rows.slice(1).forEach((row, i) => {
      const cardNumber = String(row[0] || '').trim();
      const rfid = String(row[1] || '').trim().toUpperCase();
      if (!cardNumber && !rfid) return;
      if (!cardNumber) { errors.push(t('cards.physical.messages.fileMissingCardNumber', { row: i + 2 })); return; }
      if (!rfid) { errors.push(t('cards.physical.messages.fileMissingRfid', { row: i + 2 })); return; }
      result.push({ cardNumber, rfid });
    });
    return { result, errors };
  };

  const handleFileSelect = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'csv', 'xls'].includes(ext)) {
      setBatchError(t('cards.physical.messages.unsupportedFile'));
      return;
    }
    setBatchFile(file);
    setBatchError('');
    setBatchPreview([]);
    const { result, errors } = await parseCardFile(file);
    setBatchPreview(result);
    setBatchError(errors.join('\n'));
  };

  const handleBatchSubmit = async () => {
    if (!canManagePhysicalCards) return;
    if (batchPreview.length === 0) return;
    setBatchLoading(true);
    setBatchResult(null);
    try {
      const res = await adminAPI.batchAddPhysicalCards(batchPreview);
      const data = res.data;
      const insertedCount = data.inserted ?? data.success ?? batchPreview.length;
      setBatchResult({ success: insertedCount, failed: data.failed ?? 0, failedList: data.failedList || [] });
      if (insertedCount > 0) loadPhysicalCards();
    } catch {
      let success = 0, failed = 0, failedList = [];
      for (const card of batchPreview) {
        try {
          await adminAPI.addPhysicalCard({ rfid: card.rfid, cardNumber: card.cardNumber });
          success++;
        } catch (e) {
          failed++;
          failedList.push(`${card.cardNumber}/${card.rfid}: ${e.response?.data?.message || t('cards.physical.messages.failed')}`);
        }
      }
      setBatchResult({ success, failed, failedList });
      if (success > 0) loadPhysicalCards();
    } finally {
      setBatchLoading(false);
    }
  };

  const openBalanceModal = (operation) => {
    if (!canManagePhysicalCardBalance) {
      setPhysicalMsg({
        type: 'error',
        text: t('cards.physical.messages.amountUnavailable', { defaultValue: 'Card amount cannot be set by this account' })
      });
      return;
    }
    setBalanceModal({ operation });
    setBalanceAmount('');
    setBalanceScope(selectedPhysicalCardIds.size > 0 ? 'selected' : (operation === 'write' ? 'unwritten_unbound' : 'all_unbound'));
    setBalanceRangeStart('');
    setBalanceRangeEnd('');
    setBalanceReason('');
    setBalanceResult(null);
    setBalancePreview(null);
  };

  const buildBalancePayload = () => ({
    operation: balanceModal?.operation || 'write',
    amount: Number(balanceAmount),
    scope: balanceScope,
    selectionMode: balanceScope,
    reason: balanceReason,
    cardIds: balanceScope === 'selected' ? Array.from(selectedPhysicalCardIds) : [],
    rangeStart: balanceScope === 'range' ? balanceRangeStart.trim() : undefined,
    rangeEnd: balanceScope === 'range' ? balanceRangeEnd.trim() : undefined,
    bindingStatus: bindingStatusFilter,
    status: statusFilter,
    search: appliedSearchQuery.trim() || undefined
  });

  const validateBalancePayload = () => {
    const amount = Number(balanceAmount);
    if (!Number.isFinite(amount) || amount < 0 || (balanceModal?.operation === 'write' && amount <= 0)) {
      setBalanceResult({
        type: 'error',
        text: balanceModal?.operation === 'write'
          ? t('cards.physical.messages.writeAmountPositive')
          : t('cards.physical.messages.amountNonNegative')
      });
      return false;
    }
    if (balanceScope === 'selected' && selectedPhysicalCardIds.size === 0) {
      setBalanceResult({ type: 'error', text: t('cards.physical.balance.selectFirst') });
      return false;
    }
    if (balanceScope === 'range' && (!balanceRangeStart.trim() || !balanceRangeEnd.trim())) {
      setBalanceResult({ type: 'error', text: t('cards.physical.messages.rangeRequired') });
      return false;
    }
    if (balanceModal?.operation === 'reset' && !balanceReason.trim()) {
      setBalanceResult({ type: 'error', text: t('cards.physical.balance.reasonRequired') });
      return false;
    }
    return true;
  };

  const handleBalancePreview = async () => {
    if (!validateBalancePayload()) return;

    setBalancePreviewLoading(true);
    setBalanceResult(null);
    setBalancePreview(null);

    try {
      const res = await adminAPI.previewPhysicalCardBalanceOperation(buildBalancePayload());
      setBalancePreview(res.data?.data || null);
    } catch (error) {
      setBalanceResult({
        type: 'error',
        text: error.response?.data?.message || t('cards.physical.messages.previewFailed')
      });
    } finally {
      setBalancePreviewLoading(false);
    }
  };

  const handleBalanceSubmit = async () => {
    if (!validateBalancePayload()) return;
    if (!balancePreview) {
      setBalanceResult({ type: 'error', text: t('cards.physical.messages.previewRequired') });
      return;
    }

    const ok = window.confirm(t('cards.physical.balance.confirm', { count: balancePreview.updatableCount || 0 }));
    if (!ok) return;

    setBalanceLoading(true);
    setBalanceResult(null);

    try {
      const res = balanceModal?.operation === 'reset'
        ? await adminAPI.batchResetPhysicalCardBalances(buildBalancePayload())
        : await adminAPI.batchWritePhysicalCardBalances(buildBalancePayload());
      const data = res.data?.data || {};
      setBalanceResult({
        type: 'success',
        text: t('cards.physical.balance.result', {
          updated: data.updatedCount || 0,
          skippedBound: data.skippedBoundCount || 0,
          skippedWritten: data.skippedWrittenCount || 0
        })
      });
      setBalancePreview(null);
      loadPhysicalCards(currentPage);
    } catch (error) {
      setBalanceResult({
        type: 'error',
        text: error.response?.data?.message || t('cards.physical.messages.balanceOperationFailed')
      });
    } finally {
      setBalanceLoading(false);
    }
  };

  // Removed legacy station-card handlers.

  return (
    <div className="p-8 space-y-8 text-white">
      <header className="flex justify-between items-end">
        <div>
          <h2 className="text-3xl font-bold flex items-center gap-3">
            <CreditCard className="text-cyan-400" size={32} />
            {t('cards.title')}
          </h2>
        </div>
      </header>

      {financeView && (
        <section className={`rounded-xl border p-4 ${financeView === 'sales' ? 'border-amber-300 bg-amber-50 text-amber-950' : 'border-cyan-300 bg-cyan-50 text-cyan-950'}`} role="status">
          <h3 className="font-black">{t(`cards.financeView.${financeView}.title`)}</h3>
          <p className="mt-1 text-sm leading-6">{t(`cards.financeView.${financeView}.description`)}</p>
        </section>
      )}

      {financeView === 'balance' && physicalSummary.financial && (
        <section className="space-y-3" aria-label={t('cards.financeView.balance.breakdownTitle')}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {[
              ['circulating', 'border-cyan-400', 'bg-cyan-50 text-cyan-950'],
              ['inventory', 'border-slate-400', 'bg-slate-50 text-slate-950'],
              ['blocked', 'border-amber-400', 'bg-amber-50 text-amber-950'],
              ['anomaly', 'border-rose-400', 'bg-rose-50 text-rose-950'],
              ['excludedTrial', 'border-violet-400', 'bg-violet-50 text-violet-950']
            ].map(([key, borderClass, colorClass]) => {
              const group = physicalSummary.financial[key] || {};
              return (
                <article key={key} className={`rounded-xl border-l-4 p-4 ${borderClass} ${colorClass}`}>
                  <p className="text-sm font-black">{t(`cards.financeView.balance.groups.${key}.title`)}</p>
                  <p className="mt-2 break-words text-2xl font-black">{formatCardAmount(group.remainingBalance)}</p>
                  <p className="mt-2 text-xs font-bold opacity-80">
                    {t('cards.financeView.balance.cardCount', { count: Number(group.totalCards || 0).toLocaleString('en-PH') })}
                  </p>
                  <p className="mt-2 text-xs leading-5 opacity-80">{t(`cards.financeView.balance.groups.${key}.description`)}</p>
                </article>
              );
            })}
          </div>
          <p className="rounded-lg border border-cyan-200 bg-cyan-50 p-3 text-sm font-bold leading-6 text-cyan-950">
            {t('cards.financeView.balance.mainAmountRule')}
          </p>
        </section>
      )}

      {/* Only physical water cards are displayed. */}

      {/* Physical water-card summary. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-slate-800/50 p-6 rounded-2xl border-b-4 border-cyan-400 border-slate-700/50 relative overflow-hidden group">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-1">{t('cards.physical.stats.totalCards')}</p>
          <h3 className="text-3xl font-black text-white">{Number(physicalSummary.totalCards || totalCards || 0).toLocaleString('en-PH')} <small className="text-xs font-normal opacity-30">{t('cards.physical.units.cards')}</small></h3>
          <CreditCard className="absolute -right-2 -bottom-2 text-6xl opacity-5 text-cyan-400 group-hover:opacity-10 transition-opacity" size={80} />
        </div>
        <div className="bg-slate-800/50 p-6 rounded-2xl border-b-4 border-emerald-500 border-slate-700/50 relative overflow-hidden group">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-1">{t('cards.physical.stats.boundCards')}</p>
          <h3 className="text-3xl font-black text-white">
            {Number(physicalSummary.boundCards || 0).toLocaleString('en-PH')}
            <small className="text-xs font-normal opacity-30"> {t('cards.physical.units.cards')}</small>
          </h3>
          <CheckCircle2 className="absolute -right-2 -bottom-2 text-6xl opacity-5 text-emerald-500 group-hover:opacity-10 transition-opacity" size={80} />
        </div>
      </div>

      {/* Physical water-card actions. */}
      <div className="space-y-6">
          {/* Add a physical card. */}
          {canManagePhysicalCards && <div className="bg-slate-800/50 border border-white/10 rounded-2xl p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <CreditCard className="text-cyan-400" size={20} />
              {t('cards.physical.add.title')}
            </h3>
            <div className="flex flex-col gap-3 mb-3 xl:flex-row">
              <input
                type="text"
                value={newCardNumber}
                onChange={e => setNewCardNumber(e.target.value)}
                placeholder={t('cards.physical.add.cardNumberPlaceholder')}
                className="w-full min-w-0 xl:flex-1 bg-slate-900 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-400"
              />
              <input
                type="text"
                value={newRfid}
                onChange={e => setNewRfid(e.target.value)}
                placeholder={t('cards.physical.add.rfidPlaceholder')}
                className="w-full min-w-0 xl:flex-1 bg-slate-900 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                onKeyDown={e => e.key === 'Enter' && handleAddPhysicalCard()}
              />
              {canManagePhysicalCardBalance && (
                <input
                  type="number"
                  min="0"
                  value={newInitialValue}
                  onChange={e => setNewInitialValue(e.target.value)}
                  placeholder={t('cards.physical.add.initialValuePlaceholder')}
                  className="w-full xl:w-44 bg-slate-900 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400"
                  onKeyDown={e => e.key === 'Enter' && handleAddPhysicalCard()}
                />
              )}
              <button
                onClick={handleAddPhysicalCard}
                className="w-full justify-center xl:w-auto bg-cyan-400 text-slate-900 font-bold px-6 py-2.5 rounded-xl text-sm flex items-center gap-2 hover:bg-cyan-300 transition-all"
              >
                <Plus size={16} />
                {t('cards.physical.add.submit')}
              </button>
            </div>
            <button
              onClick={() => { setShowBatchPhysical(true); setBatchFile(null); setBatchPreview([]); setBatchError(''); setBatchResult(null); if (batchFileRef.current) batchFileRef.current.value = ''; }}
              className="flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300 border border-amber-400/30 hover:border-amber-400/60 px-4 py-2 rounded-xl transition-all"
            >
              <Upload size={15} />
              {t('cards.physical.batch.title')}
            </button>
            {physicalMsg && (
              <p className={`mt-3 text-sm font-medium ${
                physicalMsg.type === 'success' ? 'text-emerald-400' : 'text-red-400'
              }`}>{physicalMsg.text}</p>
            )}
          </div>}

          {/* Trial card, retaining the legacy KRIAL backend field. */}
          {canManagePhysicalCards && <div className="bg-slate-800/50 border border-cyan-400/20 rounded-2xl p-6">
            <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <CheckCircle2 className="text-cyan-400" size={20} />
                  {t('cards.physical.krial.title')}
                </h3>
                <p className="mt-1 text-sm leading-5 text-slate-400">{t('cards.physical.krial.description')}</p>
              </div>
              <span className="rounded-xl border border-cyan-300 bg-cyan-100 px-3 py-2 text-xs font-bold text-cyan-900">
                {t('cards.physical.krial.policy')}
              </span>
            </div>
            <div className="flex flex-col gap-3 xl:flex-row">
              <input
                type="text"
                value={krialCardLookup}
                onChange={e => setKrialCardLookup(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAssignKrialTrial()}
                placeholder={t('cards.physical.krial.lookupPlaceholder')}
                className="w-full min-w-0 xl:flex-1 bg-slate-900 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
              />
              <input
                type="datetime-local"
                value={krialTrialStartAt}
                onChange={e => setKrialTrialStartAt(e.target.value)}
                className="w-full xl:w-64 bg-slate-900 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                title={t('cards.physical.krial.trialStartTitle')}
              />
              <button
                onClick={handleAssignKrialTrial}
                disabled={krialLoading}
                className="w-full justify-center xl:w-auto bg-cyan-400 text-slate-900 font-bold px-6 py-2.5 rounded-xl text-sm flex items-center gap-2 hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-60 transition-all"
              >
                {krialLoading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                {krialLoading ? t('cards.physical.krial.saving') : t('cards.physical.krial.submit')}
              </button>
            </div>
            {krialMsg && (
              <p className={`mt-3 text-sm font-medium ${
                krialMsg.type === 'success' ? 'text-emerald-400' : 'text-red-400'
              }`}>{krialMsg.text}</p>
            )}
          </div>}

          {/* Physical card list. */}
          <div className="bg-slate-800/50 border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-white/10 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <h3 className="shrink-0 whitespace-nowrap font-bold text-lg">{t('cards.physical.list.title')}</h3>
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setCurrentPage(1);
                        setAppliedSearchQuery(searchQuery);
                        loadPhysicalCards(1, searchQuery);
                      }
                    }}
                    placeholder={t('cards.physical.list.searchPlaceholder')}
                    className="w-72 bg-slate-900 border border-white/10 rounded-xl pl-10 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                  />
                </div>
                <button
                  onClick={() => {
                    setCurrentPage(1);
                    setAppliedSearchQuery(searchQuery);
                    loadPhysicalCards(1, searchQuery);
                  }}
                  className="px-4 py-2 rounded-xl bg-cyan-400 text-slate-900 text-sm font-bold hover:bg-cyan-300 transition-colors"
                >
                  {t('common.search')}
                </button>
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setAppliedSearchQuery('');
                      setCurrentPage(1);
                      loadPhysicalCards(1, '');
                    }}
                    className="px-3 py-2 rounded-xl bg-slate-700 text-slate-300 text-sm hover:bg-slate-600 transition-colors"
                  >
                    {t('common.clear', { defaultValue: 'Clear' })}
                  </button>
                )}
                {/* Status filter. */}
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-slate-900 border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-cyan-400"
                >
                  <option value="all">{t('cards.physical.filters.allStatus')}</option>
                  <option value="Active">{t('cards.physical.filters.active')}</option>
                  <option value="Inactive">{t('cards.physical.filters.inactive')}</option>
                </select>
                <select
                  value={bindingStatusFilter}
                  onChange={(e) => {
                    setBindingStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-slate-900 border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-cyan-400"
                >
                  <option value="all">{t('cards.physical.filters.allBinding')}</option>
                  <option value="unbound">{t('cards.physical.filters.unbound')}</option>
                  <option value="bound">{t('cards.physical.filters.bound')}</option>
                </select>
                {selectedPhysicalCardCount > 0 && (
                  <div data-testid="physical-card-selected-count" className="flex items-center gap-2 rounded-xl border border-cyan-600 bg-cyan-100 px-3 py-2 text-xs font-bold text-cyan-950">
                    <span>{t('cards.physical.selectedCount', { count: selectedPhysicalCardCount })}</span>
                    <button
                      onClick={() => setSelectedPhysicalCardIds(new Set())}
                      className="rounded px-1 font-black text-cyan-900 hover:bg-cyan-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-700"
                    >
                      {t('common.clear', { defaultValue: 'Clear' })}
                    </button>
                  </div>
                )}
                {canManagePhysicalCardBalance && (
                  <>
                    <button
                      onClick={() => openBalanceModal('write')}
                      className="px-4 py-2 rounded-xl bg-emerald-400 text-slate-900 text-sm font-bold hover:bg-emerald-300 transition-colors"
                    >
                      {t('cards.physical.actions.batchWrite')}
                    </button>
                    <button
                      onClick={() => openBalanceModal('reset')}
                      className="px-4 py-2 rounded-xl bg-amber-400 text-slate-900 text-sm font-bold hover:bg-amber-300 transition-colors"
                    >
                      {t('cards.physical.actions.batchReset')}
                    </button>
                  </>
                )}
                {/* Pagination controls. */}
                <div className="flex items-center gap-2 text-sm">
                  <button
                    onClick={() => loadPhysicalCards(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg transition-colors"
                  >
                    {t('common.previous', { defaultValue: 'Previous' })}
                  </button>
                  <span className="text-slate-400">
                    {t('cards.physical.pagination', { page: currentPage, totalPages, total: totalCards })}
                  </span>
                  <button
                    onClick={() => loadPhysicalCards(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg transition-colors"
                  >
                    {t('common.next', { defaultValue: 'Next' })}
                  </button>
                </div>
                <button onClick={() => loadPhysicalCards(currentPage)} className="text-slate-400 hover:text-white transition-colors">
                  <RefreshCw size={16} />
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
            {physicalLoading ? (
              <div className="flex items-center justify-center py-12 text-slate-400">
                <Loader2 className="animate-spin mr-2" size={20} /> {t('common.loading')}
              </div>
            ) : physicalCards.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-slate-500">{t('cards.physical.list.empty')}</div>
            ) : (
              <table className="w-full min-w-[980px]">
                <thead>
                  <tr className="text-xs text-slate-400 uppercase border-b border-white/5">
                    {canManagePhysicalCardBalance && <th className="p-4 text-left w-12">
                      <input
                        type="checkbox"
                        checked={allVisibleSelectableChecked}
                        disabled={visibleSelectableCards.length === 0}
                        onChange={(e) => toggleVisiblePhysicalCards(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-500 bg-slate-900 text-cyan-400"
                        title={t('cards.physical.table.selectPageTitle')}
                      />
                    </th>}
                    <th className="p-4 text-left">{t('cards.physical.table.cardNumber')}</th>
                    <th className="p-4 text-left">{t('cards.physical.table.cardBalance')}</th>
                    <th className="p-4 text-left">{t('cards.physical.table.userBalance')}</th>
                    <th className="p-4 text-left">{t('cards.physical.table.krialStatus')}</th>
                    <th className="p-4 text-left">{t('cards.physical.table.bindingStatus')}</th>
                    <th className="p-4 text-left">{t('cards.physical.table.boundUser')}</th>
                    <th className="p-4 text-left">{t('cards.physical.table.boundAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {physicalCards.map(card => {
                      const boundToUser = isCardBoundToUser(card);

    // Derive binding state.
                      let bindStatus = '';
                      let bindClass = '';
                      const krialStatus = getKrialStatus(card);
                      if (card.status === 'Inactive') {
                        bindStatus = t('cards.physical.status.inactive');
                        bindClass = 'bg-red-500/20 text-red-400';
                      } else if (card.userId) {
                        bindStatus = t('cards.physical.status.bound');
                        bindClass = 'bg-emerald-500/20 text-emerald-400';
                      } else {
                        bindStatus = t('cards.physical.status.unbound');
                        bindClass = 'bg-slate-500/20 text-slate-400';
                      }

                      return (
                    <tr key={card.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      {canManagePhysicalCardBalance && <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selectedPhysicalCardIds.has(card.id)}
                          disabled={!isCardEligibleForBalanceSelection(card)}
                          onChange={(e) => togglePhysicalCardSelection(card.id, e.target.checked)}
                          className="h-4 w-4 rounded border-slate-500 bg-slate-900 text-cyan-400 disabled:opacity-30"
                          title={!isCardEligibleForBalanceSelection(card)
                            ? t('cards.physical.table.statusLockedTitle', { defaultValue: 'Inactive, lost, or damaged cards cannot be selected' })
                            : (boundToUser && isCardAmountWritten(card)
                              ? t('cards.physical.table.boundLockedTitle')
                              : t('cards.physical.table.selectCardTitle'))}
                        />
                      </td>}
                      <td className="p-4 font-mono font-bold text-amber-300">{card.cardNumber || <span className="text-slate-600 text-xs">{t('cards.physical.table.notEntered')}</span>}</td>
                      <td className="p-4 font-mono font-bold text-emerald-300">
                        {formatCardAmount(getPhysicalCardDisplayBalance(card))}
                      </td>
                      <td className="p-4 font-mono font-bold text-cyan-300">
                        {card.user ? formatCardAmount(card.userAccountBalance ?? card.user?.balance) : <span className="text-slate-500">-</span>}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${krialStatus.className}`}>
                          {krialStatus.label}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${bindClass}`}>
                          {bindStatus}
                        </span>
                      </td>
                      <td className="p-4 text-slate-300 text-sm">
                        {card.user ? `${card.user.name || ''} ${card.user.phone || ''}`.trim() : <span className="text-slate-500">{t('cards.physical.status.unbound')}</span>}
                      </td>
                      <td className="p-4 text-slate-400 text-sm">
                        {formatCardDate(card.boundAt)}
                      </td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
            )}
            </div>
          </div>
        </div>

      {/* Bulk physical card import modal */}
      {canManagePhysicalCards && showBatchPhysical && (
        <AdminModalSurface onClose={() => setShowBatchPhysical(false)}>
          <div className="bg-slate-800 text-slate-100 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl border border-white/10" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-bold text-lg flex items-center gap-2"><Upload size={18} className="text-amber-400" />{t('cards.physical.batch.title')}</h3>
              <button type="button" aria-label="Close dialog" onClick={() => setShowBatchPhysical(false)} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {/* File upload area. */}
              <input
                ref={batchFileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={e => handleFileSelect(e.target.files[0])}
              />
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  isDragging ? 'border-cyan-400 bg-cyan-900/20' :
                  batchFile ? 'border-emerald-500 bg-emerald-900/10' :
                  'border-slate-600 hover:border-slate-400 bg-slate-900/30'
                }`}
                onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={e => { e.preventDefault(); setIsDragging(false); handleFileSelect(e.dataTransfer.files[0]); }}
                onClick={() => !batchFile && batchFileRef.current?.click()}
              >
                {batchFile ? (
                  <div className="flex flex-col items-center gap-3">
                    <FileText size={36} className="text-emerald-400" />
                    <p className="font-bold text-emerald-300">{batchFile.name}</p>
                    <p className="text-xs text-slate-400">{(batchFile.size / 1024).toFixed(1)} KB</p>
                    <button
                      onClick={e => { e.stopPropagation(); setBatchFile(null); setBatchPreview([]); setBatchError(''); setBatchResult(null); if (batchFileRef.current) batchFileRef.current.value = ''; }}
                      className="text-xs text-slate-400 hover:text-white border border-slate-600 hover:border-slate-400 px-3 py-1 rounded-lg transition-colors"
                    >{t('cards.physical.batch.chooseAgain')}</button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3 text-slate-400">
                    <Upload size={36} className="opacity-60" />
                    <p className="text-sm">{t('cards.physical.batch.dropHint')} <span className="text-cyan-400 font-medium">{t('cards.physical.batch.clickUpload')}</span></p>
                    <p className="text-xs text-slate-500">{t('cards.physical.batch.fileFormatHint')}</p>
                    <p className="text-xs text-slate-500">{t('cards.physical.batch.headerHint')}</p>
                  </div>
                )}
              </div>
              {batchError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                  <p className="text-red-400 text-xs font-mono whitespace-pre-wrap">{batchError}</p>
                </div>
              )}
              {batchPreview.length > 0 && !batchResult && (
                <div>
                  <p className="text-xs font-bold text-slate-400 mb-2">{t('cards.physical.batch.preview', { count: batchPreview.length })}</p>
                  <div className="max-h-48 overflow-y-auto rounded-xl border border-white/10">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-500 border-b border-white/5">
                          <th className="p-2 text-left">#</th>
                          <th className="p-2 text-left">{t('cards.physical.table.cardNumber')}</th>
                          <th className="p-2 text-left">{t('cards.physical.table.rfid')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchPreview.map((c, i) => (
                          <tr key={i} className="border-b border-white/5">
                            <td className="p-2 text-slate-500">{i + 1}</td>
                            <td className="p-2 font-mono text-amber-300">{c.cardNumber}</td>
                            <td className="p-2 font-mono text-cyan-300">{c.rfid}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {batchResult && (
                <div className={`rounded-xl p-4 ${batchResult.failed === 0 ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                  <p className="font-bold text-sm mb-1">
                    {t('cards.physical.batch.importResult', {
                      success: batchResult.success,
                      failed: batchResult.failed
                    })}
                  </p>
                  {batchResult.failedList.length > 0 && (
                    <p className="text-xs font-mono text-red-400 whitespace-pre-wrap">{batchResult.failedList.join('\n')}</p>
                  )}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-white/10 flex gap-3 justify-end">
              <button onClick={() => setShowBatchPhysical(false)} className="px-5 py-2 rounded-xl text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium transition-colors">{t('common.close')}</button>
              {!batchResult && (
                <button
                  onClick={handleBatchSubmit}
                  disabled={batchPreview.length === 0 || batchLoading || !!batchError}
                  className="px-6 py-2 rounded-xl text-sm bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold flex items-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {batchLoading ? <><Loader2 size={15} className="animate-spin" />{t('cards.physical.batch.importing')}</> : <><Upload size={15} />{t('cards.physical.batch.confirmImport', { count: batchPreview.length })}</>}
                </button>
              )}
              {batchResult && (
                <button onClick={() => { setBatchResult(null); setBatchFile(null); setBatchPreview([]); setBatchError(''); if (batchFileRef.current) batchFileRef.current.value = ''; }} className="px-5 py-2 rounded-xl text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium">{t('cards.physical.batch.importAgain')}</button>
              )}
            </div>
          </div>
        </AdminModalSurface>
      )}

      {/* Batch balance-management dialog. */}
      {balanceModal && (
        <AdminModalSurface onClose={() => setBalanceModal(null)}>
          <div data-testid="physical-card-balance-modal" className="admin-card w-full max-w-xl rounded-2xl border border-[var(--admin-border-subtle)] shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[var(--admin-border-subtle)] p-6">
              <h3 className="flex items-center gap-2 text-lg font-bold text-[var(--admin-text-primary)]">
                <Database size={18} className={balanceModal.operation === 'reset' ? 'text-amber-400' : 'text-emerald-400'} />
                {balanceModal.operation === 'reset' ? t('cards.physical.actions.batchReset') : t('cards.physical.actions.batchWrite')}
              </h3>
              <button onClick={() => setBalanceModal(null)} className="rounded-lg p-2 text-[var(--admin-text-secondary)] hover:bg-[var(--admin-surface-soft)] hover:text-[var(--admin-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--admin-accent)]"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                <p className="text-sm font-bold text-red-700">{t('cards.physical.balance.safetyTitle')}</p>
                <p className="mt-1 text-sm leading-relaxed text-red-600">
                  {t('cards.physical.balance.safetyDesc')}
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-[var(--admin-text-secondary)]">{t('cards.physical.balance.amount')}</label>
                <input
                  type="number"
                  min="0"
                  value={balanceAmount}
                  onChange={e => { setBalanceAmount(e.target.value); setBalancePreview(null); }}
                  placeholder={t('cards.physical.balance.amountPlaceholder')}
                  className="admin-input w-full rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-400"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-[var(--admin-text-secondary)]">{t('cards.physical.balance.scope')}</label>
                <select
                  value={balanceScope}
                  onChange={e => { setBalanceScope(e.target.value); setBalancePreview(null); }}
                  className="admin-input w-full rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-cyan-400"
                >
                  <option value="selected" disabled={selectedPhysicalCardCount === 0}>{t('cards.physical.balance.selectedScope', { count: selectedPhysicalCardCount })}</option>
                  <option value="range">{t('cards.physical.balance.rangeScope')}</option>
                  {balanceModal.operation === 'write' && (
                    <option value="unwritten_unbound">{t('cards.physical.balance.unwrittenUnbound')}</option>
                  )}
                  <option value="all_unbound">{t('cards.physical.balance.allUnbound')}</option>
                  <option value="current_filter">{t('cards.physical.balance.currentFilter')}</option>
                </select>
                {balanceScope === 'range' && (
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <input
                      value={balanceRangeStart}
                      onChange={e => { setBalanceRangeStart(e.target.value); setBalancePreview(null); }}
                      placeholder={t('cards.physical.balance.rangeStartPlaceholder')}
                      className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                    />
                    <input
                      value={balanceRangeEnd}
                      onChange={e => { setBalanceRangeEnd(e.target.value); setBalancePreview(null); }}
                      placeholder={t('cards.physical.balance.rangeEndPlaceholder')}
                      className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                    />
                  </div>
                )}
                {balanceScope === 'current_filter' && (
                  <p className="mt-2 text-xs leading-relaxed text-slate-400">
                    {t('cards.physical.balance.currentFilterHint')}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-[var(--admin-text-secondary)]">{t('cards.physical.balance.reason')}</label>
                <textarea
                  value={balanceReason}
                  onChange={e => { setBalanceReason(e.target.value); setBalancePreview(null); }}
                  placeholder={t('cards.physical.balance.reasonPlaceholder')}
                  className="admin-input min-h-[88px] w-full rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-cyan-400"
                />
              </div>

              {balancePreview && (
                <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4 text-sm text-cyan-950">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div><p className="text-sm font-semibold text-cyan-700">{t('cards.physical.balance.matchedCount')}</p><p className="font-bold">{balancePreview.matchedCount || 0}</p></div>
                    <div><p className="text-sm font-semibold text-cyan-700">{t('cards.physical.balance.updatableCount')}</p><p className="font-bold">{balancePreview.updatableCount || 0}</p></div>
                    <div><p className="text-sm font-semibold text-cyan-700">{t('cards.physical.balance.skippedBound')}</p><p className="font-bold">{balancePreview.skippedBoundCount || 0}</p></div>
                    <div><p className="text-sm font-semibold text-cyan-700">{t('cards.physical.balance.skippedWritten')}</p><p className="font-bold">{balancePreview.skippedWrittenCount || 0}</p></div>
                  </div>
                  {balancePreview.sampleCards?.length > 0 && (
                    <div className="mt-3 max-h-24 overflow-y-auto rounded-lg bg-slate-900 p-2 font-mono text-xs text-white">
                      {balancePreview.sampleCards.map(card => (
                        <div key={card.id}>{card.cardNumber || t('cards.physical.table.notEntered')} - {formatCardAmount(card.balance)}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {balanceResult && (
                <div className={`rounded-xl p-3 text-sm ${
                  balanceResult.type === 'success'
                    ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300'
                    : 'bg-red-500/10 border border-red-500/30 text-red-300'
                }`}>
                  {balanceResult.text}
                </div>
              )}
            </div>
            <div className="flex flex-wrap justify-end gap-3 border-t border-[var(--admin-border-subtle)] p-6">
              <button onClick={() => setBalanceModal(null)} className="admin-secondary-button rounded-xl px-5 py-2 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-slate-300">{t('common.close')}</button>
              <button
                data-testid="physical-card-preview-button"
                onClick={handleBalancePreview}
                disabled={balancePreviewLoading || balanceLoading}
                className="flex items-center gap-2 rounded-xl border border-cyan-800 bg-cyan-700 px-5 py-2 text-sm font-black text-[#ffffff] transition-colors hover:bg-cyan-800 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-300 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-200 disabled:text-slate-700 disabled:opacity-100"
              >
                {balancePreviewLoading ? <><Loader2 size={15} className="animate-spin" />{t('cards.physical.balance.previewing')}</> : t('cards.physical.balance.previewFirst')}
              </button>
              <button
                data-testid="physical-card-execute-button"
                onClick={handleBalanceSubmit}
                disabled={balanceLoading || !balancePreview || (balancePreview.updatableCount || 0) === 0}
                className="flex items-center gap-2 rounded-xl border border-cyan-500 bg-cyan-400 px-6 py-2 text-sm font-black text-slate-950 transition-colors hover:bg-cyan-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-300 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-200 disabled:text-slate-700 disabled:opacity-100"
              >
                {balanceLoading ? <><Loader2 size={15} className="animate-spin" />{t('cards.physical.balance.processing')}</> : t('cards.physical.balance.execute')}
              </button>
            </div>
          </div>
        </AdminModalSurface>
      )}

    </div>
  );
}
